import classNames from 'classnames/bind';
import { MAX_MULTI_CITY_COUNT, MIN_MULTI_CITY_COUNT } from 'common/utils';
import { Modal, ModalFuncContext, ModalPosition } from 'contexts/modalFunc';
import { ISearchParams, useSearchParamsContext } from 'contexts/searchParams';
import { Dispatch, Fragment, SetStateAction, useContext } from 'react';
import { ITabContent } from '..';
import { City, DepartureDate, Passenger, Swap } from '../select';
import styles from './index.module.scss';

const cx = classNames.bind(styles);

export const multicityDeleteHandler =
  (setSearchParams: Dispatch<SetStateAction<ISearchParams>>) =>
  (index: number) => {
    setSearchParams(prev => {
      const { itinerary } = prev;
      itinerary.splice(index, 1);
      return { ...prev, itinerary };
    });
  };

export const multicityAddHandler =
  (setSearchParams: Dispatch<SetStateAction<ISearchParams>>) => () => {
    setSearchParams(prev => {
      const newItinerary = prev.itinerary.map(iti => ({ ...iti }));
      const prevFirst = newItinerary[0];
      const prevLast = newItinerary[newItinerary.length - 1];
      if (prevFirst.departureCity?.code === prevLast.arrivalCity?.code) {
        delete prevLast.arrivalCity;
      }
      newItinerary.push({
        departureCity: prevLast.arrivalCity,
        arrivalCity: prevFirst.departureCity,
      });
      return {
        ...prev,
        itinerary: newItinerary,
      };
    });
  };

export default function MultiCity({
  clickCodePrefix,
  isMultiCityExpanded,
  handleMultiCityExpanded,
}: ITabContent) {
  const setModal = useContext(ModalFuncContext);
  const {
    searchParams: { itinerary },
    setSearchParams,
  } = useSearchParamsContext();
  const modalHandler = (type: Modal, index?: number) => () =>
    setModal({ type, payload: { index }, position: ModalPosition.MAIN });
  const onAdd = multicityAddHandler(setSearchParams);
  const onDelete = multicityDeleteHandler(setSearchParams);

  return (
    <div
      className={cx('Multicity')}
      data-multicity-maxCityCount={
        itinerary.length === MAX_MULTI_CITY_COUNT && isMultiCityExpanded
      }
    >
      {isMultiCityExpanded ? (
        <div className={cx('itinerary')}>
          {itinerary.map((_, index) => (
            <Fragment key={index}>
              <div className={cx('flight')}>
                <div className={cx('routes')}>
                  <City
                    className={cx('route')}
                    multiCity
                    departure
                    index={index}
                    clickCodePrefix={clickCodePrefix}
                    onClick={modalHandler(Modal.DEPARTURES)} // [D] 모든곳 검색인 경우 - modalHandler(Modal.DEPARTURES_EVERYWHERE)
                  />
                  <City
                    className={cx('route')}
                    multiCity
                    index={index}
                    clickCodePrefix={clickCodePrefix}
                    onClick={modalHandler(Modal.ARRIVALS)}
                  />
                  <Swap
                    className={cx('swap')}
                    index={index}
                    clickCodePrefix={clickCodePrefix}
                  />
                </div>
                <DepartureDate
                  className={cx('date')}
                  index={index}
                  multiCity
                  clickCodePrefix={clickCodePrefix}
                  isMultiCityExpanded={isMultiCityExpanded}
                  onClick={modalHandler(Modal.CALENDAR, index)}
                />
                {index > MIN_MULTI_CITY_COUNT - 1 && (
                  <button
                    type="button"
                    className={cx('remove')}
                    aria-label="일정 삭제"
                    data-event-nlog="click"
                    data-event-area-code={`${clickCodePrefix}.del3`}
                    onClick={() => onDelete(index)}
                  />
                )}
              </div>
              <hr />
            </Fragment>
          ))}
        </div>
      ) : (
        <div className={cx('itinerary')}>
          <Fragment key={0}>
            <div className={cx('flight')}>
              <div className={cx('routes')}>
                <City
                  className={cx('route')}
                  multiCity
                  departure
                  index={0}
                  clickCodePrefix={clickCodePrefix}
                  onClick={handleMultiCityExpanded}
                />
                <City
                  className={cx('route')}
                  multiCity
                  index={itinerary.length - 1}
                  clickCodePrefix={clickCodePrefix}
                  onClick={handleMultiCityExpanded}
                />
                <div className={cx('summary_flight')}>
                  <span className={cx('txt')}>{itinerary.length}개 항공</span>
                </div>
              </div>
              <DepartureDate
                className={cx('date')}
                index={0}
                multiCity
                clickCodePrefix={clickCodePrefix}
                isMultiCityExpanded={isMultiCityExpanded}
                onClick={handleMultiCityExpanded}
              />
            </div>
          </Fragment>
        </div>
      )}

      {itinerary.length < MAX_MULTI_CITY_COUNT && isMultiCityExpanded && (
        <div className={cx('add')}>
          <button
            type="button"
            aria-label={`${itinerary.length + 1}여정 추가하기`}
            data-event-nlog="click"
            data-event-area-code={`${clickCodePrefix}.add3`}
            onClick={onAdd}
          />
        </div>
      )}
      <div className={cx('options')}>
        <Passenger className={cx('option')} clickCodePrefix={clickCodePrefix} />
      </div>
    </div>
  );
}
